import { type ReactNode, useMemo } from 'react';

import { Box, Group, Text, getStylesRef } from '@mantine/core';

import { Link, matchPath, useLocation } from 'react-router-dom';

import { withFeatureFlag } from '../../../../../shared/components/FeatureFlagGuard';
import type { FeatureFlag } from '../../../../../shared/entities/FeatureFlags';

interface SiderBarItemProps {
  label: string;
  icon: ReactNode;
  to: string;
  activePath: string[];
  featureFlag?: FeatureFlag;
  isCompact?: boolean;
}

function SafeSiderBarItem({
  label,
  icon,
  to,
  isCompact,
  activePath,
}: SiderBarItemProps) {
  const { pathname } = useLocation();
  const isActive = useMemo(
    () => activePath.some(path => matchPath(path, pathname)),
    [pathname, activePath],
  );

  return (
    <Box
      component={Link}
      to={to}
      sx={{
        textDecoration: 'none',
      }}
    >
      <Group
        spacing="02"
        align="center"
        position={isCompact ? 'center' : 'left'}
        px="03"
        py="02"
        w="100%"
        sx={theme => ({
          transition: 'all 0.2s ease-in-out',
          borderRadius: theme.radius.md,
          color: theme.colors.gray[9],
          '& svg': {
            color: 'inherit',
            transition: 'color 0.2s ease-in-out',
          },
          ...(isActive && {
            background: theme.colors.primary[1],
            color: theme.colors.primary[7],
            [`& .${getStylesRef('label')}`]: {
              fontWeight: 500,
            },
          }),
          ...(!isCompact && {
            '&:hover': {
              background: theme.colors.primary[1],
              color: theme.colors.primary[7],
              [`& .${getStylesRef('label')}`]: {
                fontWeight: 500,
              },
            },
          }),
        })}
      >
        {icon}
        {!isCompact && (
          <Text
            component="span"
            size="sm"
            color="inherit"
            sx={{ ref: getStylesRef('label') }}
          >
            {label}
          </Text>
        )}
      </Group>
    </Box>
  );
}

export function SideBarItem({ featureFlag, ...props }: SiderBarItemProps) {
  return withFeatureFlag(<SafeSiderBarItem {...props} />, featureFlag);
}
